import * as React from 'react';
import { applyRunwayTheme } from './globalStyles';
import { antGrey } from './globalStyles';
import { updateNavTypeUserProperty } from '../../utils/analytics';

export type ThemeMode = 'light' | 'mixed' | 'dark';

const STORAGE_KEY = 'runway.themeMode';

function getInitialThemeMode(): ThemeMode {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored === 'light' || stored === 'mixed' || stored === 'dark') {
            return stored;
        }
    } catch (_e) {
        // localStorage unavailable
    }

    return 'light';
}

let _currentMode: ThemeMode = getInitialThemeMode();

export function getCurrentThemeMode(): ThemeMode {
    return _currentMode;
}

export function getCurrentSidebarTheme(): 'light' | 'dark' {
    return _currentMode === 'light' ? 'light' : 'dark';
}

export function getCurrentSidebarColors(): SidebarColors {
    return getCurrentSidebarTheme() === 'light' ? SIDEBAR_COLORS_LIGHT : SIDEBAR_COLORS_DARK;
}

type ThemeModeContextValue = {
    mode: ThemeMode;
    setMode: (mode: ThemeMode) => void;
};

const defaultContextValue: ThemeModeContextValue = {
    mode: 'light',
    setMode: () => {},
};

const ThemeModeContext = React.createContext<ThemeModeContextValue>(defaultContextValue);

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [mode, setMode] = React.useState<ThemeMode>(() => getInitialThemeMode());

    const handleSetMode = React.useCallback((newMode: ThemeMode) => {
        _currentMode = newMode;
        setMode(newMode);
        updateNavTypeUserProperty(newMode);
    }, []);

    React.useEffect(() => {
        const dataTheme = mode === 'dark' ? 'dark' : 'light';
        const sidebarTheme = getCurrentSidebarTheme();
        document.documentElement.setAttribute('data-theme', dataTheme);
        document.documentElement.setAttribute('data-sidebar-theme', sidebarTheme);
        applyRunwayTheme(dataTheme as 'light' | 'dark');

        try {
            window.localStorage.setItem(STORAGE_KEY, mode);
        } catch {
            // ignore
        }
    }, [mode]);

    return <ThemeModeContext.Provider value={{ mode, setMode: handleSetMode }}>{children}</ThemeModeContext.Provider>;
};

export function useThemeMode(): ThemeModeContextValue {
    return React.useContext(ThemeModeContext);
}

export function useSidebarTheme(): 'light' | 'dark' {
    const { mode } = useThemeMode();
    return mode === 'light' ? 'light' : 'dark';
}

export interface SidebarColors {
    bg: string;
    // bgSecondary: string;
    dropdownBg: string;
    border: string;
    text: string;
    textSecondary: string;
    textMuted: string;
    icon: string;
    iconMuted: string;
    selectedBg: string;
    connectorBg: string;
    segmentedBg: string;
    segmentedActiveBg: string;
    menuHoverBg: string;
    menuActiveBg: string;
    dropdownHoverBg: string;
    headerControlsBg: string;
    skippedReleaseDateText: string;
    releaseDateText: string;
    stepChildrenActiveBg: string;
    platformMuted: string;
}

const SIDEBAR_COLORS_DARK: SidebarColors = {
    bg: '#262626',
    // bgSecondary: '#303030',
    dropdownBg: '#000000',
    border: '#737373',
    text: '#ffffff',
    textSecondary: '#bfbfbf',
    textMuted: '#8c8c8c',
    icon: '#bfbfbf',
    iconMuted: '#8c8c8c',
    selectedBg: '#434343',
    menuHoverBg: antGrey.grey9,
    menuActiveBg: antGrey.grey8,
    dropdownHoverBg: '#262626',
    connectorBg: 'rgba(140, 140, 140, 0.5)',
    segmentedBg: 'rgba(0, 0, 0, 0.5)',
    segmentedActiveBg: '#595959',
    headerControlsBg: 'transparent',
    skippedReleaseDateText: antGrey.grey8,
    releaseDateText: antGrey.grey6,
    stepChildrenActiveBg: 'rgba(89, 89, 89, 0.25)',
    platformMuted: antGrey.grey8,
};

const SIDEBAR_COLORS_LIGHT: SidebarColors = {
    bg: '#F0F0F0',
    // bgSecondary: '#FFB800',
    dropdownBg: '#ffffff',
    border: '#BFBFBF',
    text: '#262626',
    textSecondary: '#8C8C8C',
    textMuted: '#8c8c8c',
    icon: '#8C8C8C',
    iconMuted: '#8c8c8c',
    selectedBg: '#f0f0f0',
    menuHoverBg: '#e6e6e6',
    menuActiveBg: '#dbdbdb',
    dropdownHoverBg: '#f2f2f2',
    connectorBg: 'rgba(140, 140, 140, 0.5)',
    segmentedBg: 'rgba(217, 217, 217, 0.5)',
    segmentedActiveBg: '#FAFAFA',
    headerControlsBg: 'rgba(255, 255, 255, 0.5)',
    skippedReleaseDateText: antGrey.grey7,
    releaseDateText: antGrey.grey10,
    stepChildrenActiveBg: 'rgba(217, 217, 217, 0.5)',
    platformMuted: antGrey.grey6,
};

export function useSidebarColors(): SidebarColors {
    const sidebarTheme = useSidebarTheme();
    return sidebarTheme === 'light' ? SIDEBAR_COLORS_LIGHT : SIDEBAR_COLORS_DARK;
}

export function useChartTheme(): 'dark' | 'default' {
    const { mode } = useThemeMode();
    return mode === 'dark' ? 'dark' : 'default';
}
export interface ChartColors {
    gridLine: string;
    axisLabel: string;
    annotationText: string;
    tooltipBg: string;
    tooltipText: string;
    secondarySeries: string;
}

const CHART_COLORS_LIGHT: ChartColors = {
    gridLine: '#f0f0f0', // antGrey.grey4
    axisLabel: '#8c8c8c', // antGrey.grey7
    annotationText: '#595959', // antGrey.grey8
    tooltipBg: '#595959', // antGrey.grey8
    tooltipText: '#ffffff',
    secondarySeries: '#bfbfbf', // antGrey.grey6
};

const CHART_COLORS_DARK: ChartColors = {
    gridLine: '#303030', // dark mode grey4 equivalent
    axisLabel: '#bfbfbf', // dark mode grey7 equivalent
    annotationText: '#d9d9d9', // dark mode grey8 equivalent
    tooltipBg: '#262626', // dark mode grey8 equivalent
    tooltipText: '#f5f5f5',
    secondarySeries: '#595959', // dark mode grey6 equivalent
};

export function useChartColors(): ChartColors {
    const { mode } = useThemeMode();
    return mode === 'dark' ? CHART_COLORS_DARK : CHART_COLORS_LIGHT;
}
